import React, { useEffect, useState } from 'react'
import { Column } from 'primereact/column'
import { Fieldset } from 'primereact/fieldset'

import {
  CustomDataTable,
  CustomDataTableHeader,
  CustomMultiSelect,
  Loading,
  Periodo,
  TextBtn,
} from '../../../../components'
import { tableCellCentered } from '../../../../assets/js/styles'
import {
  formatIsoDateToLocale,
  getPeriodoFromLastXDays,
  parseToMysqlFormatWithTime,
} from '../../../../services/util/date.service'
import { downloadFile } from '../../../../services/util/download.service'

/**
 * @param {Function} fetchLogs - Função async que recebe (pInicial, pFinal) e retorna os logs.
 * @param {Function} downloadLog - Função async que recebe o id do log e retorna o arquivo.
 * @param {Function} showMessageBox - Dispatch para mensagens de erro.
 * @param {boolean} showTpFilter - Exibe ou oculta o filtro de TPs (default: true).
 */
export default function XmlImportLogTab({
  fetchLogs,
  downloadLog,
  showMessageBox,
  showTpFilter = true,
}) {
  const { pInicial, pFinal } = getPeriodoFromLastXDays(31)
  const [periodo, setPeriodo] = useState({
    dataInicial: new Date(pInicial.setHours(0, 0, 0, 0)),
    dataFinal: new Date(pFinal.setHours(23, 59, 59, 0)),
  })
  const [isLoading, setIsLoading] = useState(false)
  const [logs, setLogs] = useState([])
  const [filteredLogs, setFilteredLogs] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [downloadingLogId, setDownloadingLogId] = useState(null)

  // Filtros
  const [originOptions, setOriginOptions] = useState([])
  const [selectedOrigins, setSelectedOrigins] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [tpOptions, setTpOptions] = useState([])
  const [selectedTps, setSelectedTps] = useState([])
  const [empresaOptions, setEmpresaOptions] = useState([])
  const [selectedEmpresas, setSelectedEmpresas] = useState([])

  useEffect(() => {
    onPesquisar(periodo.dataInicial, periodo.dataFinal)
  }, [])

  async function onPesquisar(dataInicial, dataFinal) {
    setIsLoading(true)
    setPeriodo({ dataInicial, dataFinal })
    try {
      const pInicialStr = parseToMysqlFormatWithTime(
        new Date(new Date(dataInicial).setHours(0, 0, 0, 0)),
      )
      const pFinalStr = parseToMysqlFormatWithTime(
        new Date(new Date(dataFinal).setHours(23, 59, 59, 0)),
      )
      const result = await fetchLogs(pInicialStr, pFinalStr)
      setLogs(result)
      setFilteredLogs(result)
      setSelectedOrigins([])
      setSelectedStatuses([])
      setSelectedTps([])
      setSelectedEmpresas([])
    } catch (error) {
      showMessageBox(null, error)
    } finally {
      setIsLoading(false)
    }
  }

  function formatLogDate(value) {
    if (typeof value !== 'string' || !value) return '-'

    const date = formatIsoDateToLocale(value)
    return date.isValid ? date.toFormat('dd/MM/yyyy HH:mm:ss') : '-'
  }

  async function onDownload(log) {
    const logId = Number(log && log.id)
    const fileName = log && log.file_name

    if (!Number.isInteger(logId) || logId <= 0) {
      showMessageBox(null, 'Não foi possível identificar o log para realizar o download.')
      return
    }
    if (typeof fileName !== 'string' || !fileName.trim()) {
      showMessageBox(null, 'O nome original do arquivo não está disponível neste log.')
      return
    }
    if (typeof downloadLog !== 'function') {
      showMessageBox(null, 'O download do arquivo não está disponível nesta tela.')
      return
    }

    setDownloadingLogId(logId)
    try {
      const file = await downloadLog(logId)
      if (!file || typeof file.size !== 'number' || file.size === 0) {
        throw new Error('O conteúdo retornado para o arquivo está vazio ou é inválido.')
      }
      downloadFile(file, fileName)
    } catch (error) {
      showMessageBox(null, error && error.message ? error.message : error)
    } finally {
      setDownloadingLogId(null)
    }
  }

  // Atualizar opções de filtro com base nos dados
  useEffect(() => {
    let filtered = logs

    // Origem
    const origins = [...new Set(logs.map(l => l.origin).filter(Boolean))].sort()
    setOriginOptions(origins.map(o => ({ label: o, value: o })))

    if (selectedOrigins.length) {
      filtered = filtered.filter(l => selectedOrigins.includes(l.origin))
    }

    // Status
    const statuses = [...new Set(logs.map(l => l.status).filter(Boolean))].sort()
    setStatusOptions(statuses.map(s => ({ label: s, value: s })))

    if (selectedStatuses.length) {
      filtered = filtered.filter(l => selectedStatuses.includes(l.status))
    }

    // TPs
    const tps = [...new Map(filtered.filter(l => l.tp_id).map(l => [l.tp_id, l])).values()]
      .map(l => ({ label: `${l.tp_id} - ${l.tp_name || 'N/A'}`, value: l.tp_id }))
      .sort((a, b) => a.label.localeCompare(b.label))
    setTpOptions(tps)

    if (selectedTps.length) {
      filtered = filtered.filter(l => selectedTps.includes(l.tp_id))
    }

    // Empresas
    const empresas = [
      ...new Map(filtered.filter(l => l.company_id).map(l => [l.company_id, l])).values(),
    ]
      .map(l => ({ label: `${l.company_id} - ${l.company_name || 'N/A'}`, value: l.company_id }))
      .sort((a, b) => a.label.localeCompare(b.label))
    setEmpresaOptions(empresas)

    if (selectedEmpresas.length) {
      filtered = filtered.filter(l => selectedEmpresas.includes(l.company_id))
    }

    setFilteredLogs(filtered)
  }, [logs, selectedOrigins, selectedStatuses, selectedTps, selectedEmpresas])

  return (
    <div>
      <div className="p-grid">
        <div className="p-col-5">
          <Periodo
            periodoInicial={periodo.dataInicial}
            periodoFinal={periodo.dataFinal}
            onPesquisar={onPesquisar}
          />
        </div>
      </div>

      <Fieldset legend="Filtros" toggleable collapsed={false} className="p-col-12">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="p-col-2" style={{ minWidth: 200 }}>
            <CustomMultiSelect
              marginTop={10}
              label="Origem"
              options={originOptions}
              value={selectedOrigins}
              onChange={setSelectedOrigins}
            />
          </div>
          <div className="p-col-2" style={{ minWidth: 200 }}>
            <CustomMultiSelect
              marginTop={10}
              label="Status"
              options={statusOptions}
              value={selectedStatuses}
              onChange={setSelectedStatuses}
            />
          </div>
          {showTpFilter && (
            <div className="p-col-3" style={{ minWidth: 250 }}>
              <CustomMultiSelect
                marginTop={10}
                label="TPs"
                options={tpOptions}
                value={selectedTps}
                onChange={setSelectedTps}
              />
            </div>
          )}
          <div className="p-col-3" style={{ minWidth: 250 }}>
            <CustomMultiSelect
              marginTop={10}
              label="Empresas"
              options={empresaOptions}
              value={selectedEmpresas}
              onChange={setSelectedEmpresas}
            />
          </div>
        </div>
      </Fieldset>

      <div style={{ marginTop: 15 }}>
        {isLoading ? (
          <div className="text-center">
            <Loading size="md" />
          </div>
        ) : (
          <CustomDataTable
            value={filteredLogs}
            paginator
            rows={15}
            globalFilter={globalFilter}
            header={<CustomDataTableHeader onChange={setGlobalFilter} value={globalFilter} />}
            emptyMessage="Nenhum log encontrado para o período selecionado"
          >
            <Column field="origin" header="Origem" style={tableCellCentered(90)} />
            <Column field="status" header="Status" style={tableCellCentered(100)} />
            <Column field="error_type" header="Tipo" style={tableCellCentered(160)} />
            <Column
              field="error_message"
              header="Mensagem"
              style={{ maxWidth: 300, wordBreak: 'break-word' }}
            />
            <Column
              field="file_name"
              header="Nome do Arquivo"
              style={{ maxWidth: 200, wordBreak: 'break-word' }}
            />
            <Column
              field="company_name"
              header="Empresa"
              style={tableCellCentered(150)}
              body={row => row.company_name || '-'}
            />
            <Column
              field="tp_name"
              header="Tp"
              style={tableCellCentered(150)}
              body={row => row.tp_name || '-'}
            />
            <Column field="nNF" header="Identificador" style={tableCellCentered(120)} />
            <Column
              field="created_at"
              header="Data"
              style={tableCellCentered(160)}
              body={row => formatLogDate(row.created_at)}
            />
            <Column
              header="Arquivo"
              style={tableCellCentered(90)}
              body={row =>
                downloadingLogId === Number(row.id) ? (
                  <Loading size="xs" />
                ) : (
                  <TextBtn onClick={() => onDownload(row)}>Baixar</TextBtn>
                )
              }
            />
          </CustomDataTable>
        )}
      </div>
    </div>
  )
}
